import { useEffect, useState } from "react";
import { type DepartmentDTO, getUsersApi, listDepartments } from "@praesto/core";
import { Checkbox } from "@praesto/ui-kit";

/**
 * Hotel-scoped department picker: lists the chosen hotel's ACTIVE departments by
 * `displayName` and stores their `code`. This is the single assignment surface
 * shared by user-create / invite / profile — it replaces the old free-text field
 * that let codes and display labels mix in `membership.departments`.
 */
export function DepartmentMultiSelect(props: {
  hotelId: string;
  value: ReadonlyArray<string>;
  onChange: (codes: ReadonlyArray<string>) => void;
  /** Shown but not changeable, e.g. an owner's departments seen by a manager. */
  disabled?: boolean;
}) {
  const [depts, setDepts] = useState<ReadonlyArray<DepartmentDTO>>([]);

  useEffect(() => {
    if (!props.hotelId) {
      setDepts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const r = await listDepartments(api.apiBase, props.hotelId, tok);
      if (!cancelled) setDepts(r.departments.filter((d) => d.status === "active"));
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [props.hotelId]);

  if (depts.length === 0) {
    return <p className="text-caption text-fg-muted">Nessun reparto attivo per questo hotel.</p>;
  }

  return (
    <div className="flex flex-wrap gap-3">
      {depts.map((d) => (
        <Checkbox
          key={d.code}
          label={d.displayName}
          checked={props.value.includes(d.code)}
          disabled={props.disabled}
          onChange={(e) =>
            props.onChange(
              e.target.checked ? [...props.value, d.code] : props.value.filter((x) => x !== d.code),
            )
          }
        />
      ))}
    </div>
  );
}
